import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CREATE_PROJECT_URL } from '@/constants/links';

export type OnboardingStepId = 'create-project' | 'add-application' | 'build-workflow';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  route: string;
}

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    id: 'create-project',
    title: 'Create a Project',
    description: 'Set up a resource group in your Azure subscription',
    route: CREATE_PROJECT_URL,
  },
  {
    id: 'add-application',
    title: 'Add an Application',
    description: 'Deploy a Logic App to your project',
    route: '/',
  },
  {
    id: 'build-workflow',
    title: 'Build Your First Workflow',
    description: 'Use AI or start from scratch to automate a process',
    route: '/',
  },
];

interface OnboardingState {
  completedSteps: OnboardingStepId[];
  dismissed: boolean;
  minimized: boolean;
  completeStep: (stepId: OnboardingStepId) => void;
  dismissOnboarding: () => void;
  toggleMinimized: () => void;
  resetOnboarding: () => void;
}

export const useOnboardingProgress = create<OnboardingState>()(
  persist(
    (set) => ({
      completedSteps: [],
      dismissed: false,
      minimized: false,

      completeStep: (stepId) =>
        set((state) => {
          if (state.completedSteps.includes(stepId)) return state;
          return { completedSteps: [...state.completedSteps, stepId] };
        }),

      dismissOnboarding: () => set({ dismissed: true }),

      toggleMinimized: () => set((state) => ({ minimized: !state.minimized })),

      resetOnboarding: () => set({ completedSteps: [], dismissed: false, minimized: false }),
    }),
    {
      name: 'otto-onboarding',
      // Only persist dismissed and minimized; completedSteps recalculates from actual data
      partialize: (state) => ({ dismissed: state.dismissed, minimized: state.minimized }),
    }
  )
);

/** Derived selectors */
export function getStepStatus(
  stepId: OnboardingStepId,
  completedSteps: OnboardingStepId[]
): 'completed' | 'current' | 'upcoming' {
  if (completedSteps.includes(stepId)) return 'completed';

  const stepIndex = ONBOARDING_STEPS.findIndex((s) => s.id === stepId);
  const allPriorDone = ONBOARDING_STEPS.slice(0, stepIndex).every((s) =>
    completedSteps.includes(s.id)
  );
  return allPriorDone ? 'current' : 'upcoming';
}

export function getProgressPercent(completedSteps: OnboardingStepId[]): number {
  const total = ONBOARDING_STEPS.length;
  if (total <= 0) return 0;
  return Math.round(Math.max(0, Math.min(100, (completedSteps.length / total) * 100)));
}

/**
 * Auto-sync onboarding steps from actual data presence.
 * Call from pages to mark steps complete based on what resources exist.
 */
export function syncOnboardingFromData(context: {
  hasProjects?: boolean;
  hasApps?: boolean;
  hasWorkflows?: boolean;
}): void {
  const { completeStep, completedSteps } = useOnboardingProgress.getState();
  if (context.hasProjects && !completedSteps.includes('create-project')) {
    completeStep('create-project');
  }
  if (context.hasApps && !completedSteps.includes('add-application')) {
    completeStep('add-application');
  }
  // Having an app implies the user navigated the full flow (project → app).
  // Mark the workflow step complete so onboarding doesn't persist —
  // the user clearly knows how to use the product.
  if (context.hasApps && !completedSteps.includes('build-workflow')) {
    completeStep('build-workflow');
  }
}

/** Check if onboarding should be shown (not dismissed and not all complete) */
export function shouldShowOnboarding(): boolean {
  const { dismissed, completedSteps } = useOnboardingProgress.getState();
  return !dismissed && completedSteps.length < ONBOARDING_STEPS.length;
}
